import type Phaser from 'phaser';
import { sfx } from '../audio/sfx';
import { loadProfile } from '../core/meta';
import { ensureCharmArt, ensureStoreCardArt } from '../ui/charmArtLoad';
import { decodeReadyTextures } from '../ui/decodeArt';
import { ensureDuelArt, ensureMenuArt } from '../ui/ensureTextures';
import type { GlenLoadScene } from '../ui/glenLoad';
import { blockMainThread, glenBlockMsFromLocation } from '../ui/glenLoaderOverlay';
import { installLoaderAssets, preloadLoaderAssets } from '../ui/glenPaint';
import { beginLoaderCritter } from '../ui/glenLoadPlan';
import { installLoaderPortrait } from '../ui/glenPortrait';
import { ensureHubIcons } from '../ui/hubIcons';
import { browserStore } from '../ui/loadoutStore';
import { loadPlayScenes } from './playScenes';

const WARM_PAGES = ['collection', 'events', 'friends', 'album'] as const;

/** Image decodes share the main thread with the glen loops. Keep only a few in flight. */
const GLEN_LOAD_PARALLEL = 4;

function paceLoader(scene: Phaser.Scene): () => void {
  const previous = scene.load.maxParallelDownloads;
  scene.load.maxParallelDownloads = Math.min(previous, GLEN_LOAD_PARALLEL);
  return () => {
    scene.load.maxParallelDownloads = previous;
  };
}

let entering: Promise<boolean> | null = null;

/** Scene code and paintings. The heavy audio waits until the hub is up, so it does not steal the loader's bandwidth. */
export function warmAfterTitle(scene: Phaser.Scene): void {
  void preloadLoaderAssets();
  void loadPlayScenes(scene.game);
  void ensureDuelArt(scene);
  void ensureMenuArt(scene);
}

export function duelArt(scene: Phaser.Scene): Promise<void> {
  return ensureDuelArt(scene);
}

function loaderOf(scene: Phaser.Scene): GlenLoadScene | null {
  const found = scene.scene.get('glen-load');
  return found ? (found as GlenLoadScene) : null;
}

function showLoader(scene: Phaser.Scene): void {
  if (!scene.scene.get('glen-load')) return;
  if (!scene.scene.isActive('glen-load')) scene.scene.launch('glen-load');
  scene.scene.bringToTop('glen-load');
  loaderOf(scene)?.mountOverlay();
}

/** Let the eased honey label reach the stall point before the main thread is blocked. */
function waitUntilShown(scene: Phaser.Scene, amount: number, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const start = performance.now();
    const tick = () => {
      const shown = loaderOf(scene)?.shownAmount() ?? 0;
      if (shown >= amount || performance.now() - start >= timeoutMs) {
        resolve();
        return;
      }
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

/** The frame after `create`, so the overlay stays up through the first board paint. */
function nextPaint(game: Phaser.Game): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    game.events.once('postrender', finish);
    window.setTimeout(finish, 1500);
  });
}

function paint(scene: Phaser.Scene, amount: number, tip?: string): void {
  loaderOf(scene)?.setProgress(amount, tip);
  if (scene.scene.isActive('glen-load')) scene.scene.bringToTop('glen-load');
}

function afterFrame(game: Phaser.Game): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    // A stalled paint must not leave the loader up over a hub the tests can already see.
    game.events.once('postrender', finish);
    window.setTimeout(finish, 50);
  });
}

async function warmFonts(): Promise<void> {
  const fonts = document.fonts;
  if (!fonts?.load) return;
  const sizes = [24, 26, 28, 32, 34, 36, 40];
  await Promise.all([
    ...sizes.map((size) => fonts.load(`700 ${size}px Fredoka`)),
    fonts.load('80px "Lilita One"'),
  ]).catch(() => undefined);
}

async function warmPages(scene: Phaser.Scene): Promise<void> {
  const game = scene.game;
  for (const key of WARM_PAGES) {
    const target = scene.scene.get(key);
    if (!target) continue;
    if (!target.sys.isActive() && !target.sys.isSleeping()) {
      target.sys.setVisible(false);
      await new Promise<void>((resolve) => {
        target.events.once('create', () => resolve());
        scene.scene.launch(key, { warm: true });
      });
    }
    if (scene.scene.isActive('glen-load')) scene.scene.bringToTop('glen-load');
    if (target.sys.isActive()) scene.scene.sleep(key);
    await afterFrame(game);
  }
}

/**
 * Enter the glen waits until the hub's code and paintings are in.
 * A save from a newer build stays on the title and is not overwritten.
 */
export async function enterGlen(scene: Phaser.Scene): Promise<boolean> {
  if (entering) return entering;
  entering = runEnter(scene).finally(() => {
    entering = null;
  });
  return entering;
}

async function runEnter(scene: Phaser.Scene): Promise<boolean> {
  const game = scene.game;
  let opened = false;
  const releasePace = paceLoader(scene);
  try {
    sfx.unlock();
    // One critter, the glen painting, and the carved plank. They stay ahead of the menu queue.
    beginLoaderCritter();
    await Promise.race([
      Promise.all([installLoaderPortrait(scene), installLoaderAssets(scene)]),
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, 900);
      }),
    ]);
    showLoader(scene);
    paint(scene, 0.04, 'The path into the glen');
    // Hub icons finish before the duel queue starts, so the two loads do not share one complete event.
    await ensureHubIcons(scene);
    paint(scene, 0.12, 'Calling the critters');
    warmAfterTitle(scene);
    await loadPlayScenes(game);
    paint(scene, 0.28, 'Unrolling the paintings');
    // Charm cards are pinned into the document here, before warmPages allocates
    // the wood canvases. A canvas that only the texture manager holds is
    // discarded once those panels fill the phone, and the key is left behind.
    await Promise.all([duelArt(scene), ensureMenuArt(scene), ensureCharmArt(scene), warmFonts()]);
    paint(scene, 0.7, 'Letting the paint dry');
    // `?glenBlock=1500` is a capture hook. Production omits it. The busy wait lands
    // once the label reads about 70%, which is the hitch Brandt saw on a phone.
    const stallMs = glenBlockMsFromLocation(window.location.search);
    if (stallMs > 0) {
      await waitUntilShown(scene, 0.7, 8000);
      blockMainThread(stallMs);
    }
    await decodeReadyTextures(scene.textures);
    paint(scene, 0.8, 'Listening for the chest');
    await sfx.preloadKey();
    const loaded = loadProfile(browserStore, Date.now());
    if (!loaded.ok) return false;
    paint(scene, 0.86, 'Setting the tables');
    await warmPages(scene);
    // Trick and shelf photos are images whose blob URL was already revoked.
    // The wood panels can discard those decoded pixels while the key remains,
    // which leaves an empty frame on the Store. Fetch the blank ones again.
    paint(scene, 0.94, 'Setting out the tricks');
    await ensureStoreCardArt(scene);
    paint(scene, 0.96, 'The glen is open');
    // The shell paints under the gate. The hub starts as the glen begins to fade,
    // and the gate lets go of the pointer so the hub can take the next tap.
    const shell = game.scene.getScene('shell');
    if (shell && !shell.sys.isActive() && !shell.sys.isSleeping()) {
      await new Promise<void>((resolve) => {
        shell.events.once('create', () => resolve());
        scene.scene.launch('shell');
        if (scene.scene.isActive('glen-load')) scene.scene.bringToTop('glen-load');
      });
    }
    await afterFrame(game);
    const plate = game.scene.getScene('glen-load') as GlenLoadScene | null;
    plate?.setProgress(1, 'The glen is open');
    const revealHub = async () => {
      const shellScene = game.scene.getScene('shell');
      if (shellScene) shellScene.sys.setVisible(true);
      const hub = game.scene.getScene('hub');
      if (scene.sys.isActive()) {
        const pending =
          hub && !hub.sys.isActive() && !hub.sys.isSleeping()
            ? new Promise<void>((resolve) => {
                hub.events.once('create', () => resolve());
                window.setTimeout(resolve, 4000);
              })
            : null;
        scene.scene.start('hub');
        if (game.scene.isActive('glen-load')) game.scene.bringToTop('glen-load');
        if (pending) await pending;
      }
      plate?.releaseInput();
      await nextPaint(game);
    };
    if (plate && game.scene.isActive('glen-load')) {
      opened = true;
      await plate.playOutro(revealHub);
    } else if (scene.sys.isActive()) {
      scene.scene.start('hub');
      opened = true;
    }
    return opened;
  } finally {
    releasePace();
    if (game.scene.isActive('glen-load')) game.scene.stop('glen-load');
    if (opened) sfx.preloadAfterTitle();
  }
}
